import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  ListQuotationsQuery,
  QuotationGenerationJob,
  QuotationResource,
  QuotationSortField,
  QuotationStatus,
} from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import {
  DataGrid,
  dayBoundary,
  numberFilter,
  useGridState,
  type GridColumn,
  type GridState,
} from '../../components/data-grid/DataGrid';
import {
  useQuotations,
  useQuotationCounts,
  useDownloadQuotationPdf,
  useQuotationGenerationJobs,
} from '../../api/quotations';
import { NewQuotationModal } from './NewQuotationModal';
import { EditQuotationModal } from './EditQuotationModal';
import { SendQuotationModal } from './SendQuotationModal';
import { QuotationSummary } from './QuotationItemsEditor';
import { formatMoney } from './quotation-format';

const STATUSES: { value: QuotationStatus; label: string }[] = [
  { value: 'GENERATED', label: 'Generated' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
];

type Tab = 'ALL' | QuotationStatus;

/** Grid state -> list query (filters are strings in the grid). */
function toListQuery(tab: Tab, g: GridState): ListQuotationsQuery {
  const f = g.filters;
  return {
    page: g.page,
    pageSize: g.pageSize,
    sortBy: g.sortBy as QuotationSortField,
    sortOrder: g.sortOrder,
    status: tab === 'ALL' ? undefined : tab,
    customer: f.contactName?.trim() || undefined,
    reference: f.reference?.trim() || undefined,
    totalMin: numberFilter(f.totalAmountMin),
    totalMax: numberFilter(f.totalAmountMax),
    createdAfter: dayBoundary(f.createdAtFrom, false),
    createdBefore: dayBoundary(f.createdAtTo, true),
  };
}

function statusTone(status: QuotationStatus): string | undefined {
  if (status === 'FAILED') return 'var(--color-danger)';
  if (status === 'SENT') return 'var(--color-success)';
  return undefined;
}

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

/** The expanded "Details" sub-grid: every line, then the price summary. */
export function QuotationItemsTable({ quotation }: { quotation: QuotationResource }) {
  const money = (n: number) => formatMoney(n, quotation.currency);
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table className="ne-table" style={{ minWidth: 620 }}>
          <thead>
            <tr>
              <th>Item name</th>
              <th>Price</th>
              <th>Discount</th>
              <th>Discounted price</th>
              <th>Qty</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {quotation.matchedItems.map((item, i) => (
              <tr key={`m-${item.productServiceId}-${i}`} style={{ cursor: 'default' }}>
                <td>{item.productName}</td>
                <td>{money(item.unitPrice)}</td>
                <td>{item.discountPercent ?? 0}%</td>
                <td>{money(item.discountedUnitPrice ?? item.unitPrice)}</td>
                <td>
                  {item.quantity}
                  {item.unit ? ` ${item.unit}` : ''}
                </td>
                <td>{money(item.lineTotal)}</td>
              </tr>
            ))}
            {quotation.unmatchedItems.map((item, i) => (
              <tr key={`u-${i}`} style={{ cursor: 'default' }}>
                <td>
                  {item.normalizedName}
                  {item.rawText && item.rawText !== item.normalizedName ? (
                    <div style={muted}>{item.rawText}</div>
                  ) : null}
                </td>
                <td colSpan={3} style={{ color: 'var(--color-text-muted)' }}>
                  Price on request
                </td>
                <td>
                  {item.quantity}
                  {item.unit ? ` ${item.unit}` : ''}
                </td>
                <td>—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ justifySelf: 'end', minWidth: 260 }}>
        <QuotationSummary
          grossAmount={quotation.grossAmount}
          discountAmount={quotation.discountAmount}
          totalAmount={quotation.subtotalAmount}
          currency={quotation.currency}
        />
      </div>
      {quotation.errorMessage ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
          {quotation.errorMessage}
        </p>
      ) : null}
    </div>
  );
}

function jobStatusTone(status: QuotationGenerationJob['status']): string | undefined {
  if (status === 'FAILED') return 'var(--color-danger)';
  if (status === 'DONE') return 'var(--color-success)';
  return undefined;
}

function OngoingQuotationRow({ job }: { job: QuotationGenerationJob }) {
  const ongoing = job.status === 'PENDING' || job.status === 'PROCESSING';
  const pct = Math.round((job.processedFiles / Math.max(job.totalFiles, 1)) * 100);
  return (
    <li
      style={{
        display: 'grid',
        gap: 'var(--space-1)',
        borderBottom: '1px solid var(--color-border)',
        paddingBottom: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span>
          {job.totalFiles} file{job.totalFiles === 1 ? '' : 's'}
        </span>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {new Date(job.createdAt).toLocaleString()}
        </span>
        <span className="ne-badge" style={{ color: jobStatusTone(job.status) }}>
          {job.status}
        </span>
      </div>
      {ongoing ? (
        <div
          style={{
            height: 6,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-border)',
            overflow: 'hidden',
            maxWidth: 240,
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: 'var(--color-primary)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      ) : job.status === 'FAILED' && job.errorMessage ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
          {job.errorMessage}
        </p>
      ) : job.warningMessage ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          {job.warningMessage}
        </p>
      ) : job.status === 'DONE' ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Preview ready - open "New Quotation" for this contact to resolve and send it, or check
          your notifications.
        </p>
      ) : null}
    </li>
  );
}

/**
 * Quotations: customer-originated (AI photo/PDF -> catalog-match -> PDF,
 * via the `GENERATE_QUOTATION` automation action / `COLLECT_QUOTATION`
 * flow step) and staff-generated ("New Quotation" popup, for a walk-in/
 * offline customer) alike. Built on the standard `DataGrid`: status tabs
 * with counts, a filter under each column, sorting, paging, and a details
 * sub-grid per row.
 */
export function QuotationsPage() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<QuotationResource | null>(null);
  const [sending, setSending] = useState<QuotationResource | null>(null);
  const [newQuotationOpen, setNewQuotationOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const download = useDownloadQuotationPdf();

  // Dashboard quick-action deep link (`?quickAction=new`) - auto-open the
  // modal once, then strip the param so a refresh/back doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('quickAction') === 'new') {
      setNewQuotationOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('quickAction');
        return next;
      });
    }
  }, [searchParams, setSearchParams]);

  const query = toListQuery(tab, grid);
  const quotations = useQuotations(query);
  const counts = useQuotationCounts(query);
  const jobs = useQuotationGenerationJobs();
  const ongoingOrRecentJobs = (jobs.data ?? []).filter(
    (j) => j.status === 'PENDING' || j.status === 'PROCESSING' || j.status === 'FAILED',
  );

  const toggle = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  async function onViewPdf(id: string) {
    try {
      await download.mutateAsync(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not download the PDF.');
    }
  }

  const columns: GridColumn<QuotationResource>[] = [
    {
      key: 'contactName',
      header: 'Customer',
      sortable: true,
      filter: { type: 'text', placeholder: 'Name or phone' },
      render: (q) => (
        <>
          <Link to={`/contacts/${q.contactId}`}>{q.contactDisplayName ?? 'Contact'}</Link>
          {q.contactPhone ? <div style={muted}>{q.contactPhone}</div> : null}
        </>
      ),
    },
    {
      key: 'reference',
      header: 'Reference',
      filter: { type: 'text', placeholder: 'QUO-…' },
      render: (q) => (
        <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{q.reference}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (q) => new Date(q.createdAt).toLocaleString(),
    },
    { key: 'itemCount', header: 'Items', align: 'right', render: (q) => q.itemCount },
    {
      key: 'totalAmount',
      header: 'Total',
      sortable: true,
      align: 'right',
      filter: { type: 'range' },
      render: (q) => (
        <>
          <strong>{formatMoney(q.subtotalAmount, q.currency)}</strong>
          {q.discountAmount > 0 ? (
            <div style={muted}>{formatMoney(q.discountAmount, q.currency)} off</div>
          ) : null}
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (q) => (
        <span className="ne-badge" style={{ color: statusTone(q.status) }}>
          {q.status}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (q) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
          <Button
            variant="secondary"
            aria-expanded={expanded.has(q.id)}
            aria-label={`${expanded.has(q.id) ? 'Hide' : 'Show'} details of ${q.reference}`}
            onClick={() => toggle(q.id)}
          >
            {expanded.has(q.id) ? 'Hide details' : 'Details'}
          </Button>
          <Button variant="secondary" onClick={() => setEditing(q)}>
            Edit
          </Button>
          <Button variant="secondary" onClick={() => setSending(q)}>
            {q.status === 'SENT' ? 'Re-send' : 'Send'}
          </Button>
          {q.pdfMediaId ? (
            <Button
              variant="secondary"
              onClick={() => void onViewPdf(q.id)}
              disabled={download.isPending && download.variables === q.id}
            >
              {download.isPending && download.variables === q.id ? 'Downloading…' : 'PDF'}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const tabs: { id: Tab; label: string; count: number | undefined }[] = [
    { id: 'ALL', label: 'All', count: counts.data?.all },
    ...STATUSES.map((s) => ({ id: s.value as Tab, label: s.label, count: counts.data?.[s.value] })),
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1200 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Quotations</h1>
        <span style={{ flex: 1 }} />
        <Button onClick={() => setNewQuotationOpen(true)}>New Quotation</Button>
      </div>

      {newQuotationOpen ? <NewQuotationModal onClose={() => setNewQuotationOpen(false)} /> : null}
      {editing ? <EditQuotationModal quotation={editing} onClose={() => setEditing(null)} /> : null}
      {sending ? <SendQuotationModal quotation={sending} onClose={() => setSending(null)} /> : null}
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Price quotations generated from a customer&apos;s photo, PDF, or typed item list over
        WhatsApp.
      </p>

      {ongoingOrRecentJobs.length > 0 ? (
        <Card title="Ongoing quotations">
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {ongoingOrRecentJobs.map((job) => (
              <OngoingQuotationRow key={job.id} job={job} />
            ))}
          </ul>
        </Card>
      ) : null}

      <Card title="Quotations">
        <div role="tablist" aria-label="Quotation status" className="ne-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {t.count !== undefined ? <span className="ne-tabs__count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 'var(--space-3)' }}>
          {quotations.isError ? (
            <PageState
              kind="error"
              message={quotations.error.message}
              onRetry={() => void quotations.refetch()}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={quotations.data?.items ?? []}
              rowKey={(q) => q.id}
              rowLabel={(q) => q.reference}
              total={quotations.data?.meta.total ?? 0}
              state={grid}
              onStateChange={setGrid}
              loading={quotations.isFetching}
              emptyMessage={
                Object.keys(grid.filters).length > 0 || tab !== 'ALL'
                  ? 'No quotations match these filters.'
                  : 'No quotations yet - they show up here once customers send item lists over WhatsApp, or when you create one with "New Quotation".'
              }
              renderExpanded={(q) =>
                expanded.has(q.id) ? <QuotationItemsTable quotation={q} /> : null
              }
            />
          )}
        </div>
      </Card>
    </div>
  );
}
